import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCapturedParams } from "../../lib/cleanUrl";
import { Bell, MessageSquare, Send } from "lucide-react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";

const btn = "h-11 border-erf-borderCream";

function InvitePanel({ projects, onDone }) {
  const [email, setEmail] = useState("");
  const [pick, setPick] = useState([]);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const submit = (e) => {
    e.preventDefault();
    api.post("/phase3/architect/invites", { email, project_ids: pick })
      .then((r) => { setMsg(r.data.delivery === "suppressed_test_recipient" ? "Invite recorded. Test address — no email was sent." : "Invite queued for email."); setEmail(""); setPick([]); setError(null); onDone(); })
      .catch((err) => setError(errMsg(err, "Could not send the invite.")));
  };
  return (
    <Card className="ds-card border-erf-borderCream mb-5" data-testid="arch-portal-invite-panel">
      <CardContent className="p-5 space-y-3 text-sm font-nav text-erf-dark">
        <h2 className="font-display font-semibold text-base md:text-lg">Invite an architect</h2>
        <form className="space-y-3" onSubmit={submit}>
          <label className="block">Email<Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11 max-w-md" data-testid="arch-portal-invite-email" /></label>
          <fieldset className="flex flex-wrap gap-3"><legend className="mb-1">Assign to projects (optional)</legend>
            {projects.map((p) => (
              <label key={p.id} className="flex min-h-[44px] items-center gap-2">
                <input type="checkbox" className="h-5 w-5" checked={pick.includes(p.id)} onChange={(e) => setPick(e.target.checked ? [...pick, p.id] : pick.filter((x) => x !== p.id))} data-testid={`arch-portal-invite-project-${p.id}`} />{p.name}
              </label>
            ))}
          </fieldset>
          <Button type="submit" className="h-11 bg-erf-dark text-[#FEFCF7]" data-testid="arch-portal-invite-send"><Send className="w-4 h-4 mr-1.5" aria-hidden="true" />Send invite</Button>
        </form>
        {msg && <p role="status" data-testid="arch-portal-invite-msg">{msg}</p>}
        {error && <p role="alert" className="text-[#991B1B]" data-testid="arch-portal-invite-error">{error}</p>}
      </CardContent>
    </Card>
  );
}

function Thread({ pid }) {
  const [t, setT] = useState(null);
  const [body, setBody] = useState("");
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase3/architect/projects/${pid}/thread`).then((r) => setT(r.data)), [pid]);
  useEffect(() => { load().then(() => api.post(`/phase3/architect/projects/${pid}/thread/read`)); }, [load, pid]);
  if (!t) return null;
  return (
    <div className="mt-3 space-y-2" data-testid={`arch-portal-thread-${pid}`}>
      <ul className="max-h-64 space-y-1 overflow-y-auto">
        {t.messages.length === 0 && <li>No messages yet.</li>}
        {t.messages.map((m) => <li key={m.id}><strong>{m.author_name || m.author_role}</strong>: {m.body}</li>)}
      </ul>
      <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); api.post(`/phase3/architect/projects/${pid}/thread`, { body }).then(() => { setBody(""); setError(null); load(); }).catch((err) => setError(errMsg(err, "Could not send."))); }}>
        <label className="sr-only" htmlFor={`msg-${pid}`}>Message</label>
        <Input id={`msg-${pid}`} required value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message the project team" className="h-11 flex-1" data-testid={`arch-portal-msg-input-${pid}`} />
        <Button type="submit" className="h-11 bg-erf-dark text-[#FEFCF7]" data-testid={`arch-portal-msg-send-${pid}`}>Send</Button>
      </form>
      {error && <p role="alert" className="text-[#991B1B]">{error}</p>}
    </div>
  );
}

function Submissions({ pid, architect }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ title: "", drawing_number: "" });
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get("/phase3/architect/submissions", { params: { project_id: pid } }).then((r) => setRows(r.data)), [pid]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="mt-3 space-y-2" data-testid={`arch-portal-subs-${pid}`}>
      {architect && (
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); api.post("/phase3/architect/submissions", { project_id: pid, ...f }).then(() => { setF({ title: "", drawing_number: "" }); setError(null); load(); }).catch((err) => setError(errMsg(err, "Could not create the submission."))); }}>
          <label>Title<Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1 h-11" data-testid={`arch-portal-sub-title-${pid}`} /></label>
          <label>Drawing no.<Input value={f.drawing_number} onChange={(e) => setF({ ...f, drawing_number: e.target.value })} className="mt-1 h-11 w-28" data-testid={`arch-portal-sub-number-${pid}`} /></label>
          <Button type="submit" className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid={`arch-portal-sub-add-${pid}`}>New submission</Button>
        </form>
      )}
      {error && <p role="alert" className="text-[#991B1B]">{error}</p>}
      <ul className="space-y-1">
        {rows.length === 0 && <li>No submissions yet.</li>}
        {rows.map((s) => <li key={s.id}><Link to={`/phase3/architect/submissions/${s.id}`} className="underline underline-offset-2 inline-flex min-h-[44px] items-center" data-testid={`arch-portal-sub-link-${s.id}`}>{s.drawing_number && `${s.drawing_number} · `}{s.title} · rev {s.revision_number} · {s.state_label}</Link></li>)}
      </ul>
    </div>
  );
}

function ProjectCard({ p, architect }) {
  const [open, setOpen] = useState(null);
  const stat = (label, n, id) => <div data-testid={`arch-portal-${id}-${p.id}`}><div className="text-lg font-semibold">{n}</div><div className="text-xs text-erf-dark/80">{label}</div></div>;
  return (
    <Card className="ds-card border-erf-borderCream" data-testid={`arch-portal-project-${p.id}`}>
      <CardContent className="p-5 text-sm font-nav text-erf-dark">
        <h2 className="font-display font-semibold text-base md:text-lg">{p.name}</h2>
        <p className="text-xs text-erf-dark/80">{p.location || "Location not set"} · ERF contact: {p.erf_contact}</p>
        <div className="mt-3 grid grid-cols-3 gap-3">
          {stat("Drawing packages", p.drawing_packages, "packages")}
          {stat("Revision requests", p.revision_requests, "requests")}
          {stat("RFIs awaiting answer", p.rfis_awaiting_response, "rfis")}
          {stat("Pending revisions", p.pending_revisions, "pending")}
          {stat("Awaiting ERF", p.awaiting_erf, "awaiting")}
          {stat("Unread messages", p.unread_messages, "unread")}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="outline" className={btn} aria-expanded={open === "subs"} onClick={() => setOpen(open === "subs" ? null : "subs")} data-testid={`arch-portal-open-subs-${p.id}`}>Submissions</Button>
          <Button variant="outline" className={btn} aria-expanded={open === "thread"} onClick={() => setOpen(open === "thread" ? null : "thread")} data-testid={`arch-portal-open-thread-${p.id}`}><MessageSquare className="w-4 h-4 mr-1.5" aria-hidden="true" />Messages</Button>
          <Link to={`/phase3/architect?project=${p.id}`} className="inline-flex h-11 items-center rounded-md border border-erf-borderCream bg-white px-4" data-testid={`arch-portal-docs-${p.id}`}>Documents and RFIs</Link>
        </div>
        {open === "subs" && <Submissions pid={p.id} architect={architect} />}
        {open === "thread" && <Thread pid={p.id} />}
      </CardContent>
    </Card>
  );
}

function Notifications({ reload }) {
  const [n, setN] = useState(null);
  const load = useCallback(() => api.get("/phase3/architect/notifications").then((r) => setN(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!n) return null;
  return (
    <Card className="ds-card border-erf-borderCream mb-5" data-testid="arch-portal-notifications">
      <CardContent className="p-5 text-sm font-nav text-erf-dark space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-display font-semibold text-base md:text-lg mr-auto"><Bell className="inline w-4 h-4 mr-1.5" aria-hidden="true" />Notifications <span data-testid="arch-portal-unread-count">({n.unread} unread)</span></h2>
          {n.unread > 0 && <Button variant="outline" className={btn} onClick={() => api.post("/phase3/architect/notifications/read").then(() => { load(); reload(); })} data-testid="arch-portal-mark-read">Mark all read</Button>}
        </div>
        <ul className="space-y-1 max-h-48 overflow-y-auto">
          {n.items.length === 0 && <li>Nothing new.</li>}
          {n.items.slice(0, 20).map((x) => <li key={x.id} className={x.read_at ? "text-erf-dark/80" : "font-semibold"}><Link to={x.link} className="underline underline-offset-2">{x.text}</Link></li>)}
        </ul>
      </CardContent>
    </Card>
  );
}

export default function ArchitectPortal() {
  const { user } = useAuth();
  const { invite: inviteTok } = useCapturedParams();
  const [data, setData] = useState(null);
  const [inviteMsg, setInviteMsg] = useState(null);
  const load = useCallback(() => api.get("/phase3/architect/portal").then((r) => setData(r.data)), []);
  useEffect(() => {
    const tok = inviteTok;
    if (tok) api.post("/phase3/architect/invites/redeem", { token: tok }).then((r) => setInviteMsg({ ok: true, text: `Invitation accepted. ${r.data.assigned_project_ids.length} project(s) added.` })).catch((e) => setInviteMsg({ ok: false, text: errMsg(e, "This invite could not be used.") })).finally(load);
    else load();
  }, [inviteTok, load]);
  const manager = data?.role === "manager";
  return (
    <AdminAppShell activeNav="architect-portal" user={user} title="Architect Portal" subtitle="Assigned projects, submissions and messages with ERF."
      breadcrumbs={[{ label: "Owner Command Center", to: "/owner" }, { label: "Architect Portal", to: null }]}>
      <div data-testid="arch-portal">
        {data && <div role="note" className="mb-5 rounded-xl border border-[#B45309]/40 bg-[#FFF7E6] px-4 py-3 text-sm font-nav text-[#5C3A06]" data-testid="arch-portal-disclaimer">{data.disclaimer}</div>}
        {inviteMsg && <p role={inviteMsg.ok ? "status" : "alert"} className={`mb-4 text-sm ${inviteMsg.ok ? "" : "text-[#991B1B]"}`} data-testid="arch-portal-redeem-msg">{inviteMsg.text}</p>}
        {data && <Notifications reload={load} />}
        {manager && <InvitePanel projects={data.projects} onDone={load} />}
        {data && data.projects.length === 0 && <p className="text-sm font-nav" data-testid="arch-portal-empty">No projects are assigned to you yet.</p>}
        <div className="grid gap-4 lg:grid-cols-2">
          {(data?.projects || []).map((p) => <ProjectCard key={p.id} p={p} architect={!manager} />)}
        </div>
      </div>
    </AdminAppShell>
  );
}
